import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { DateTime } from "luxon";
import { CameraIcon, PencilIcon } from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import Modal from "../../../layouts/Modal";
import Field from "./StockField";
import VehicleIcons from "../../shared/VehicleIcons/VehicleIcons";
import VehicleRecalls from "../../vehicles/VehicleRecalls";
import VehicleJobs from "../../vehicles/VehicleJobs";
import VehicleLocationDropdown from "../../vehicles/VehicleLocationDropdown";
import YardStatusDropdown from "../../awaitingInventory/YardStatusDropdown";
import Depollution from "../../../pages/Depollution/Depollution";
import ExportOffersTableRow from "../../exports/ExportOffersTableRow";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import classNames from "../../../utils/classNames";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import { useAuth } from "../../../contexts/AuthContext";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useJobsByVehicle from "../../../data/useJobsByVehicle";
import useVehiclePackageData from "../../../data/useVehiclePackageData";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import { IJob } from "../../../models/Job";
import { IVehicle } from "../../../models/Vehicle";
import { VehiclePackage } from "../../../models/VehiclePackageData";

interface StockVehicleDetailsProps {
  vehicle: IVehicle;
}

export default function StockVehicleDetails({ vehicle }: StockVehicleDetailsProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { loading, claims } = useAuth();
  const { vrmRefresh, update } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle");
  const { vehiclePackageData, getVehiclePackageData } = useVehiclePackageData(
    vehicle.id,
    "StockVehicleDetails"
  );

  const [vrmModalOpen, setVrmModalOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [exportValuesOpen, setExportValuesOpen] = useState(false);

  const [vrm, setVrm] = useState(vehicle.vrm);
  const [ewwEngineOffer, setEwwEngineOffer] = useState(vehicle.enginesWorldwideEngineOffer ?? "");
  const [ewwVfeOffer, setEwwVfeOffer] = useState(vehicle.enginesWorldwideVfeOffer ?? "");
  const [vlaEngineOffer, setVlaEngineOffer] = useState(vehicle.vlaEngineOffer ?? "");
  const [vlaVfeOffer, setVlaVfeOffer] = useState(vehicle.vlaVfeOffer ?? "");

  const justDriver = useMemo(() => {
    if (loading) return false;
    return (
      !claims?.isAdmin &&
      !claims?.isExporter &&
      !claims?.isMechanic &&
      !claims?.isStaff &&
      !!claims?.isDriver
    );
  }, [loading, claims]);

  const [hasSalvage, setHasSalvage] = useState<boolean | null>(null);
  useEffect(() => {
    if (vehiclePackageData.isLoading || !vehiclePackageData.data) return;
    if (vehicle?.valuationTradePoor != null) {
      setHasSalvage(true);
      return;
    }
    if (vehiclePackageData.data.data == null) {
      setHasSalvage(false);
      return;
    }
    setHasSalvage(vehiclePackageData.data.data?.valuationDetailsFetchedAt !== null);
  }, [vehiclePackageData.isLoading, vehiclePackageData.data, vehicle?.valuationTradePoor]);

  const handleFetchSalvage = () => {
    getVehiclePackageData.mutate(
      { vehicleId: vehicle.id, package: VehiclePackage.JustSalvage },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["lead"]);
          queryClient.invalidateQueries(["vehiclePackage", vehicle.id]);
        },
      }
    );
  };

  const handleUpdateVrm = () => {
    const updatedVehicle: IVehicle = {
      ...vehicle,
      vrm: vrm.toUpperCase().replace(/\s/g, ""),
    };
    vrmRefresh.mutate(updatedVehicle, {
      onSuccess: () => {
        setVrmModalOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => saveHandlers.onError(),
    });
  };

  const openExportValuesModal = () => {
    setEwwEngineOffer(vehicle.enginesWorldwideEngineOffer ?? "");
    setEwwVfeOffer(vehicle.enginesWorldwideVfeOffer ?? "");
    setVlaEngineOffer(vehicle.vlaEngineOffer ?? "");
    setVlaVfeOffer(vehicle.vlaVfeOffer ?? "");
    setExportValuesOpen(true);
  };

  const handleUpdateExportValues = () => {
    const updatedVehicle: IVehicle = {
      ...vehicle,
      enginesWorldwideEngineOffer: ewwEngineOffer || null,
      enginesWorldwideVfeOffer: ewwVfeOffer || null,
      vlaEngineOffer: vlaEngineOffer || null,
      vlaVfeOffer: vlaVfeOffer || null,
    };
    update.mutate(updatedVehicle, {
      onSuccess: () => {
        setExportValuesOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => saveHandlers.onError(),
    });
  };

  const title = `${vehicle.combinedMake ?? ""} ${vehicle.combinedModel ?? ""}`.trim() ||
    "Unknown Vehicle";

  const bodyLabel = [vehicle.bodyStyleDescription, vehicle.numberOfDoors ? `${vehicle.numberOfDoors} door` : null]
    .filter(Boolean)
    .join(" · ");

  const transmissionLabel = [
    vehicle.combinedForwardGears ? `${vehicle.combinedForwardGears} speed` : null,
    vehicle.combinedTransmission,
  ]
    .filter(Boolean)
    .join(" ") || "-";

  const yearRange =
    vehicle.visibilityDate || vehicle.terminateDate
      ? `${formatDateTime(vehicle.visibilityDate, { year: "numeric" }, "?")} – ${formatDateTime(vehicle.terminateDate, { year: "numeric" }, "?")}`
      : "-";

  const catalyticLabel = vehicle.catalyticResults && vehicle.catalyticResults.length > 0
    ? `${PriceFormat(Math.min(...vehicle.catalyticResults.map((x) => x.value)))} · ${PriceFormat(vehicle.catalyticAveragePrice)} · ${PriceFormat(Math.max(...vehicle.catalyticResults.map((x) => x.value)))}`
    : "Unavailable";

  const exportValuesLabel = `EWW Engine ${PriceFormat(vehicle.enginesWorldwideEngineOffer)} · EWW VFE ${PriceFormat(vehicle.enginesWorldwideVfeOffer)} · VLA Engine ${PriceFormat(vehicle.vlaEngineOffer)} · VLA VFE ${PriceFormat(vehicle.vlaVfeOffer)}`;

  const exportOffersCount = vehicle.exportOffers?.length ?? 0;

  const addressLabel = [
    vehicle.line1,
    vehicle.line2,
    vehicle.town,
    vehicle.postcode,
  ]
    .filter(Boolean)
    .join(", ");

  const { jobs } = useJobsByVehicle(vehicle.id);
  const dismantlingJobs = useMemo<IJob[]>(
    () => (jobs.data?.data ?? []).filter((j) => !j.deleted),
    [jobs.data]
  );
  const dismantlingJobsCompleted = useMemo(
    () => dismantlingJobs.filter((j) => !!j.completedOn).length,
    [dismantlingJobs]
  );
  const partsAwaitingRemoval = useMemo(
    () =>
      dismantlingJobs.reduce(
        (sum, j) => sum + (j.awaitingDismantlingPartCount ?? 0),
        0
      ),
    [dismantlingJobs]
  );
  const partsDismantled = useMemo(() => {
    return dismantlingJobs.reduce((sum, j) => {
      const categories = j.jobPartCategories ?? [];
      const removed = categories.filter((c) => !!c.partData?.removedOn).length;
      return sum + removed;
    }, 0);
  }, [dismantlingJobs]);
  const isDismantlingDone =
    dismantlingJobs.length > 0 &&
    dismantlingJobs.every((j) => {
      if (j.completedOn) return true;
      const cats = j.jobPartCategories ?? [];
      if (cats.length === 0) return false;
      return cats.every((c) => {
        const part = c.partData;
        if (!part) return true;
        return !!part.removedOn || !!part.isDamaged;
      });
    });

  const dismantlingCardTitle = `Dismantling${
    dismantlingJobs.length > 0
      ? ` ${dismantlingJobsCompleted}/${dismantlingJobs.length} ${
          isDismantlingDone ? "✔" : ""
        }`
      : ""
  }`.trim();

  return (
    <>
      <Modal open={exportsOpen} width="max-w-2xl" setOpen={setExportsOpen}>
        <ExportOffersTableRow vehicle={vehicle} />
      </Modal>

      <Modal open={vrmModalOpen} setOpen={setVrmModalOpen} width="max-w-xl">
        <label className="block text-sm font-medium text-gray-700">
          Enter VRM to search for:
        </label>
        <input
          defaultValue={vehicle.vrm}
          onChange={(e) => setVrm(e.target.value)}
          className="mt-1 mb-5 w-full text-sm"
          type="text"
        />
        <ButtonInput
          label="Save"
          isSubmit={false}
          onClick={handleUpdateVrm}
          classes="float-right"
        />
      </Modal>

      <Modal open={exportValuesOpen} setOpen={setExportValuesOpen} width="max-w-xl">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Edit Export Values</h3>
          {[
            { label: "EWW Engine Offer", value: ewwEngineOffer, setter: setEwwEngineOffer },
            { label: "EWW VFE Offer", value: ewwVfeOffer, setter: setEwwVfeOffer },
            { label: "VLA Engine Offer", value: vlaEngineOffer, setter: setVlaEngineOffer },
            { label: "VLA VFE Offer", value: vlaVfeOffer, setter: setVlaVfeOffer },
          ].map((row) => (
            <div key={row.label}>
              <label className="block text-sm font-medium text-gray-700">
                {row.label}
              </label>
              <input
                value={row.value}
                onChange={(e) => row.setter(e.target.value)}
                className="mt-1 w-full text-sm"
                type="number"
                step="0.01"
              />
            </div>
          ))}
          <ButtonInput
            label="Save"
            isSubmit={false}
            onClick={handleUpdateExportValues}
            classes="float-right"
          />
        </div>
      </Modal>

      <div className="flex flex-col gap-4">
        {/* Hero */}
        <Card bodyClassName="p-4 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
            <div className="flex-shrink-0 w-48 h-32 bg-gray-50 rounded-md border border-gray-200 flex items-center justify-center overflow-hidden">
              {vehicle.vehicleImageUrl ? (
                <img
                  src={vehicle.vehicleImageUrl}
                  alt=""
                  className="w-full h-full object-contain"
                />
              ) : (
                <CameraIcon className="w-14 text-gray-400" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-gray-900 truncate">
                    {title}
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    {vehicle.vrm && (
                      <span className="inline-flex items-center rounded-md bg-yellow-300 px-2 py-0.5 font-mono font-semibold text-black text-sm">
                        {vehicle.vrm}
                      </span>
                    )}
                    {vehicle.dvlaYearOfManufacture && (
                      <span>{vehicle.dvlaYearOfManufacture}</span>
                    )}
                    {vehicle.modelSeries && <span>{vehicle.modelSeries}</span>}
                    {vehicle.stockNumber && (
                      <span>
                        Stock{" "}
                        <span className="font-medium text-gray-700">
                          {vehicle.stockNumber}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!loading && !justDriver && (
                    <ButtonInput
                      label="Update VRM"
                      isSubmit={false}
                      onClick={() => setVrmModalOpen(true)}
                      classes={undefined}
                      size="sm"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(`/vehicles/${vehicle.id}/edit`)}
                    className="inline-flex items-center gap-x-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    title="Edit Vehicle"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </button>
                </div>
              </div>

              {!vehicle.found && (
                <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200">
                  Vehicle not found on lookup. Update the VRM to re-attempt.
                </div>
              )}

              <div className="mt-3">
                <VehicleIcons vehicle={vehicle} hover />
              </div>
            </div>
          </div>
        </Card>

        {/* Recalls */}
        {vehicle.found && (
          <Card title="Recalls" bodyClassName="p-4">
            <VehicleRecalls vehicle={vehicle} />
          </Card>
        )}

        {/* Identification */}
        <Card title="Identification" bodyClassName="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Make">{vehicle.combinedMake ?? "-"}</Field>
            <Field label="Model">{vehicle.combinedModel ?? "-"}</Field>
            <Field label="Series">{vehicle.modelSeries ?? "-"}</Field>
            <Field label="Year">{vehicle.dvlaYearOfManufacture ?? "-"}</Field>
            <Field label="VRM">{vehicle.vrm ?? "-"}</Field>
            <Field label="VIN / Chassis">{vehicle.combinedVIN ?? "-"}</Field>
            <Field label="Stock Number">{vehicle.stockNumber ?? "-"}</Field>
            <Field label="Year Range">{yearRange}</Field>
          </div>
        </Card>

        {/* Specifications */}
        <Card title="Specifications" bodyClassName="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Engine Size">
              {vehicle.combinedEngineCapacity ? `${vehicle.combinedEngineCapacity}cc` : "-"}
            </Field>
            <Field label="Engine Code">{vehicle.engineModelCode ?? "-"}</Field>
            <Field label="Fuel Type">{vehicle.combinedFuelType ?? "-"}</Field>
            <Field label="Transmission">{transmissionLabel}</Field>
            <Field label="Transmission Code">
              {vehicle.vinvisTransmissionCode ?? "-"}
            </Field>
            <Field label="Body Type">{bodyLabel || "-"}</Field>
            <Field label="Colour Code">{vehicle.vinvisColourCode ?? "-"}</Field>
          </div>
        </Card>

        {/* Status & History */}
        <Card title="Status & History" bodyClassName="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Mileage">
              {vehicle.odometerInMiles
                ? `${vehicle.odometerInMiles.toLocaleString()} miles`
                : "-"}
            </Field>
            <Field label="MOT Expiry">
              {formatDateTime(vehicle.motExpiryDate, DateTime.DATE_SHORT, "-")}
            </Field>
            <Field label="Collected On">
              {vehicle.collectedOn
                ? formatDateTime(
                    vehicle.collectedOn,
                    vehicle.collectedOn.includes("T")
                      ? DateTime.DATETIME_SHORT
                      : DateTime.DATE_SHORT,
                    "-"
                  )
                : "Not Collected"}
            </Field>
          </div>
        </Card>

        {/* Location */}
        <Card title="Location" bodyClassName="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Yard Location">
              <VehicleLocationDropdown vehicle={vehicle} classes="w-full rounded-md text-sm" />
            </Field>
            <Field label="Yard Status">
              <YardStatusDropdown vehicle={vehicle} classes="w-full rounded-md text-sm" />
            </Field>
            <Field label="Collection Address">
              {addressLabel || <span className="text-gray-400">-</span>}
            </Field>
            <Field label="Postcode">{vehicle.postcode ?? "-"}</Field>
            {(vehicle.latitude != null || vehicle.longitude != null) && (
              <Field label="Coordinates">
                {vehicle.latitude?.toFixed(5) ?? "-"}, {vehicle.longitude?.toFixed(5) ?? "-"}
              </Field>
            )}
          </div>
        </Card>

        {/* Depollution */}
        <Depollution vehicleId={vehicle.id} collapsible />

        {/* Dismantling */}
        <Card
          title={dismantlingCardTitle}
          titleClassName={
            dismantlingJobs.length > 0 && isDismantlingDone ? "bg-green-300" : ""
          }
          bodyClassName="p-4"
        >
          {dismantlingJobs.length === 0 ? (
            <p className="text-sm text-gray-500">
              No dismantling jobs have been created for this vehicle yet.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <DismantleTile label="Jobs" value={dismantlingJobs.length} />
                <DismantleTile
                  label="Completed Jobs"
                  value={dismantlingJobsCompleted}
                  tone="green"
                />
                <DismantleTile
                  label="Parts Awaiting"
                  value={partsAwaitingRemoval}
                  tone={partsAwaitingRemoval > 0 ? "amber" : "slate"}
                />
                <DismantleTile
                  label="Parts Dismantled"
                  value={partsDismantled}
                  tone="green"
                />
              </div>
            </div>
          )}
        </Card>

        {/* Vehicle Jobs list */}
        <VehicleJobs vehicle={vehicle} />

        {/* Valuation & Export */}
        <Card title="Valuation & Export" bodyClassName="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Salvage Value">
              {hasSalvage === null ? (
                <span className="text-gray-400">Loading…</span>
              ) : hasSalvage ? (
                PriceFormat(vehicle.valuationTradePoor)
              ) : (
                <ButtonInput
                  label="Fetch"
                  isSubmit={false}
                  onClick={handleFetchSalvage}
                  classes="!py-1"
                  size="sm"
                />
              )}
            </Field>
            <Field label="Catalytic Converter (min · avg · max)">
              {catalyticLabel}
            </Field>
            <div
              onClick={openExportValuesModal}
              className={classNames(
                "md:col-span-2 rounded-md cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 transition-colors",
                "border border-dashed border-gray-200"
              )}
              title="Click to edit export values"
            >
              <Field label="Export Values (click to edit)">{exportValuesLabel}</Field>
            </div>
            <div
              onClick={() => setExportsOpen(true)}
              className="rounded-md cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 transition-colors border border-dashed border-gray-200"
              title="View export offers"
            >
              <Field label="Export Offers">
                {exportOffersCount > 0
                  ? `${exportOffersCount} ${exportOffersCount === 1 ? "offer" : "offers"}`
                  : "No offers"}
              </Field>
            </div>
          </div>
        </Card>

      </div>
    </>
  );
}

type DismantleTileTone = "slate" | "green" | "amber";

function DismantleTile({
  label,
  value,
  tone = "slate",
}: {
  label: string;
  value: number;
  tone?: DismantleTileTone;
}) {
  const toneClasses: Record<DismantleTileTone, string> = {
    slate: "bg-gray-50 text-gray-700 border-gray-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className={classNames("rounded-md border p-3", toneClasses[tone])}>
      <p className="text-xs font-medium uppercase tracking-wide opacity-75">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
    </div>
  );
}
